import { http } from '../../http';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import { __ } from '@/js/stores/expressions';

export type RetentionDataType =
    | 'InteractionData'
    | 'Coaching'
    | 'Summary'
    | 'Transcription'
    | 'AudioFile'
    | 'Ani'
    | 'FreeTextComments';

export const allowsQueueAndSkillOverrides = (dataType: RetentionDataType) =>
    dataType !== 'Coaching';

// Free text comments aren't stored yet, so the backend has no rule that purges them.
export const isRetentionRuleImplemented = (dataType: RetentionDataType) =>
    dataType !== 'FreeTextComments';

export type RetentionSetting = {
    id: number;
    dataType: RetentionDataType;
    retentionDays: number;
    queueId: string | null;
    skillId: string | null;
};

export type UpsertRetentionSettingInput = {
    id: number | null;
    dataType: RetentionDataType;
    retentionDays: number;
    queueId: string | null;
    skillId: string | null;
};

export type RetentionQueueOption = {
    id: string;
    name: string;
};

export type RetentionSkillOption = {
    id: string;
    name: string;
};

export type RetentionSettingQueuesAndSkills = {
    queues: RetentionQueueOption[];
    skills: RetentionSkillOption[];
};

export const getRetentionSettings = async (tenantId: string): Promise<RetentionSetting[]> => {
    return http
        .backoffice()
        .get<RetentionSetting[]>(
            `retention-settings/${tenantId}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-retention-settings') } })
        )
        .then((response) => response.data);
};

export const upsertRetentionSetting = async (
    tenantId: string,
    data: UpsertRetentionSettingInput
): Promise<RetentionSetting> => {
    return http
        .backoffice()
        .put<RetentionSetting>(
            `retention-settings/${tenantId}`,
            data,
            // silent: the mutation's onError shows the backend's actual `detail` message
            // instead of this interceptor's generic per-status-code toast.
            useCustomConfig({ silent: true })
        )
        .then((response) => response.data);
};

export const deleteRetentionSetting = async (tenantId: string, id: number): Promise<void> => {
    return http
        .backoffice()
        .delete(`retention-settings/${tenantId}/${id}`, useCustomConfig({ silent: true }))
        .then(() => undefined);
};

export const getRetentionDataTypes = async (): Promise<RetentionDataType[]> => {
    return http
        .backoffice()
        .get<RetentionDataType[]>('retention-settings/data-types')
        .then((response) => response.data);
};

export const getRetentionQueuesAndSkills = async (
    tenantId: string
): Promise<RetentionSettingQueuesAndSkills> => {
    return http
        .backoffice()
        .get<RetentionSettingQueuesAndSkills>(`retention-settings/queues-and-skills/${tenantId}`)
        .then((response) => response.data);
};
